import {
  AdvanceQualificationMethod,
  ApplicationProductTypeEnum,
  InlineAlertType,
  VxlActionTypenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  isNotQualified,
  isQualified,
} from 'features/advance/helpers/AdvanceProducts.helpers';
import useWhereToGo from 'features/advance/hooks/useWhereToGo';
import Amounts from 'features/advance/screens/registration/AdvanceAmountSelection/Amounts';
import {
  AmountSelectionAlertState,
  AmountSelectionInlineAlertAtom,
} from 'features/advance/state/AmountSelectionInlineAlertAtom';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const AdvanceAmountSelection = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'amountSelection',
  });

  const [{ inlineAlertStatus }, setInlineAlertStatus] = useAtom(
    AmountSelectionInlineAlertAtom,
  );

  const { bottom } = useSafeAreaInsets();
  const dismissToTab = useDismissToRootTab();
  const { segmentClient } = useAnalyticsConfiguration();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const { id } = useLocalSearchParams<{ id: string }>();

  const {
    advanceAccount,
    advanceProducts,
    fetching: fetchingWhereToGo,
    error: whereToGoError,
    hasEligibilityRestrictions,
  } = useWhereToGo();

  const activeAdvanceAccountId = advanceAccount?.activeAdvance?.id;
  const advanceAccountId = advanceAccount?.id;
  const isVAQualified = isQualified(advanceProducts);

  const hasActiveAdvanceAccount = isVAQualified
    ? advanceProducts.hasActiveAdvanceAccount
    : undefined;

  useBackHandler(goHome);

  const handleIconPressAnalytics = usePressableAnalytics({
    analyticsId: 'manageAccountClicked',
  });

  useFocusEffect(
    useCallback(() => {
      if (activeAdvanceAccountId && advanceAccountId) {
        router.replace({
          pathname: '/accounts/[id]',
          params: { id: advanceAccountId },
        });
        return;
      }

      if (isNotQualified(advanceProducts)) {
        router.replace('/varo-advance/link-account/intro');
        return;
      }
    }, [activeAdvanceAccountId, advanceAccountId, advanceProducts, router]),
  );

  if (hasEligibilityRestrictions) {
    return <Redirect href="/" />;
  }

  if (hasActiveAdvanceAccount === false) {
    return <Redirect href="/varo-advance/employment-info" />;
  }

  const handleTermsAndConditionAction = () => {
    router.push({
      pathname: '/terms-and-conditions',
      params: {
        agreementType: ApplicationProductTypeEnum.Advance,
      },
    });
  };

  const handleIconPress = () => {
    handleIconPressAnalytics();
    router.navigate({
      pathname: '/accounts/advance-details',
      params: { id: id ?? advanceAccountId },
    });
  };

  const { ddEligible, externalAccountEligible } = isVAQualified
    ? advanceProducts.options.reduce(
        (prevValue, currentValue) => {
          if (
            currentValue.qualificationMethod ===
            AdvanceQualificationMethod.DirectDeposit
          ) {
            return {
              ...prevValue,
              ddEligible: [...prevValue.ddEligible, currentValue],
            };
          } else if (
            currentValue.qualificationMethod ===
            AdvanceQualificationMethod.ExternalAccount
          ) {
            return {
              ddEligible: prevValue.ddEligible,
              externalAccountEligible: [
                ...prevValue.externalAccountEligible,
                currentValue,
              ],
            };
          }

          return prevValue;
        },
        { ddEligible: [], externalAccountEligible: [] },
      )
    : { ddEligible: [], externalAccountEligible: [] };

  const shouldShowIncreaseYourLimitsAlert =
    isVAQualified &&
    !!advanceProducts.inlineAlert?.__typename &&
    inlineAlertStatus !== AmountSelectionAlertState.SUCCESSFUL_LINK;

  const linkedAccountDoesNotQualifyInlineAlert =
    inlineAlertStatus === AmountSelectionAlertState.SUCCESSFUL_LINK &&
    !externalAccountEligible.length &&
    !shouldShowIncreaseYourLimitsAlert;

  return (
    <ScrollView
      contentContainerStyle={{
        ...styles.scrollContainer,
        paddingBottom: bottom,
      }}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: 'Advance',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              trailingAccessories={
                <IconButton
                  iconOnPress={handleIconPress}
                  iconButtonAccessibilityLabel="Account Details"
                  iconKey="SETTINGS"
                />
              }
              primaryOnPress={goHome}
              {...props}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetchingWhereToGo}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          whereToGoError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: () => {
                  goHome();
                },
              }}
            />
          ) : null
        }
      />

      <View style={styles.container}>
        <View>
          <TitleLgText
            numberOfLines={0}
            style={styles.title}
            aria-label={translatedText('title')}
          >
            {translatedText('title')}
          </TitleLgText>

          {shouldShowIncreaseYourLimitsAlert ? (
            <View style={styles.inLineAlertContainer}>
              <InlineAlert
                titleText={advanceProducts.inlineAlert?.title ?? ''}
                bodyText={advanceProducts.inlineAlert?.body ?? ''}
                alertType={InlineAlertType.Info}
                onCardPress={() => {
                  setInlineAlertStatus({
                    inlineAlertStatus: AmountSelectionAlertState.AWAITING_LINK,
                  });
                  if (
                    advanceProducts.inlineAlert?.bannerNavigation
                      ?.analyticsEvent
                  ) {
                    const { name, properties } = parseGQLAnalyticsEvent(
                      advanceProducts.inlineAlert.bannerNavigation
                        .analyticsEvent,
                    );
                    void segmentClient.logEvent(name, properties);
                  }

                  if (
                    advanceProducts.inlineAlert?.bannerNavigation
                      ?.__typename === VxlActionTypenames.RouteNavigationAction
                  ) {
                    // this is a server driven route and can't be type checked at compile time
                    // cast to Href to prevent type error.
                    router.dismissTo(
                      advanceProducts.inlineAlert.bannerNavigation
                        .route as Href,
                    );
                  } else {
                    router.dismissTo('/varo-advance/increase-your-limits');
                  }
                }}
                isImageVisible={false}
              />
            </View>
          ) : null}

          {linkedAccountDoesNotQualifyInlineAlert ? (
            <View style={styles.inLineAlertContainer}>
              <InlineAlert
                titleText={translatedText('inlineAlert.title')}
                bodyText={translatedText('inlineAlert.body')}
                alertType={InlineAlertType.Info}
                onDismiss={() => {
                  setInlineAlertStatus({
                    inlineAlertStatus: AmountSelectionAlertState.NONE,
                  });
                }}
                isImageVisible={false}
              />
            </View>
          ) : null}

          {isVAQualified && externalAccountEligible.length > 0 ? (
            <>
              {ddEligible.length > 0 ? (
                <>
                  <TitleSmText>
                    {translatedText('externalAccountsAmountListTitle')}
                  </TitleSmText>
                  <BodyBaseText style={styles.bodyTextStyle}>
                    {translatedText('externalAccountsAmountsListBody')}
                  </BodyBaseText>
                </>
              ) : null}
              <Amounts options={externalAccountEligible} />
            </>
          ) : null}

          {isVAQualified && ddEligible.length > 0 ? (
            <>
              {externalAccountEligible.length > 0 ? (
                <TitleSmText style={styles.titleStyle}>
                  {translatedText('ddAmountListTitle')}
                </TitleSmText>
              ) : null}
              <Amounts options={ddEligible} />
            </>
          ) : null}
        </View>

        <View>
          <View style={styles.termsAndCondition}>
            <BodyXSText numberOfLines={4}>
              {translatedText('forFullPriceDetailsSee')}
              <HyperlinkXsText onPress={handleTermsAndConditionAction}>
                {translatedText('termsAndConditions')}
              </HyperlinkXsText>
            </BodyXSText>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  bodyTextStyle: {
    color: theme.color.text.secondary,
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.s,
  },
  container: {
    flex: 1,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  inLineAlertContainer: {
    paddingBottom: theme.spacing.m,
  },
  scrollContainer: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  termsAndCondition: {
    paddingTop: theme.spacing.m,
    color: theme.color.text.secondary,
    flexShrink: 1,
  },
  title: {
    marginBottom: theme.spacing.m,
  },
  titleStyle: {
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.xl,
  },
});

export default AdvanceAmountSelection;
